import React, { useRef, useState, ChangeEvent } from 'react';
import styled from 'styled-components';

import { CategoriesMobileDisplayOption, RadiusOption } from 'types/graphql';
import useErnie from 'shared/hooks/use-ernie';
import { ERNIE_TYPES } from 'shared/constants';
import Button from 'shared/modals/button';
import useImageUploader from 'shared/hooks/use-image-uploader';
import { useUI } from 'src/hooks/use-ui';
import { useDispensary } from 'src/hooks/use-dispensary';
import { VisuallyHidden } from 'src/components/visually-hidden';

import { LargeCard } from 'src/dispensary-admin/customize/components/preview/home-page/categories-v2/cards/large-card';
import { CompactCard } from 'src/dispensary-admin/customize/components/preview/home-page/categories-v2/cards/compact-card';
import { StackedCard } from 'src/dispensary-admin/customize/components/preview/home-page/categories-v2/cards/stacked-card';

import { Category } from 'src/dispensary-admin/customize/categories/categories.types';
import { categoryStockPhotos } from '../constants';
import { ModalScreen } from './modal-screen';

type UploadImageProps = {
  category: Category;
  setSelectedImage: (image: string) => void;
  setIsUploadImageModalOpen: (isOpen: boolean) => void;
};

export const UploadImage = ({
  category,
  setSelectedImage,
  setIsUploadImageModalOpen,
}: UploadImageProps): JSX.Element => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isFileUploading, setIsFileUploading] = useState(false);
  const uploadImage = useImageUploader();
  const UI = useUI();
  const { updateDispensaryProperty, saveDispensaryInfo } = UI;
  const dispensary = useDispensary();
  const customCategoryPhotos = dispensary.customCategoryPhotos ?? [];
  const showErnie = useErnie();

  const handleClickFileInput = (): void => {
    if (!fileInputRef.current) {
      return;
    }

    fileInputRef.current.click();
  };

  const handleUploadFile = async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];

    if (!file) {
      showErnie('No file selected', ERNIE_TYPES.ERROR);
      return;
    }

    setIsFileUploading(true);

    const imageUrl = await uploadImage(file);

    if (!imageUrl) {
      showErnie('Error uploading image', ERNIE_TYPES.ERROR);
      setIsFileUploading(false);
      return;
    }

    updateDispensaryProperty('customCategoryPhotos', [
      ...customCategoryPhotos,
      { url: imageUrl, category: category.category, collectionId: category.collectionId },
    ]);

    const success = await saveDispensaryInfo();

    if (success) {
      setSelectedImage(imageUrl);
      setIsUploadImageModalOpen(false);
    } else {
      showErnie(`There was a problem saving your image.`, ERNIE_TYPES.ERROR);
    }

    setIsFileUploading(false);
  };

  return (
    <ModalScreen title='Upload Image' description='Tip: For best results use the following size guidelines:'>
      <Examples>
        <Example>
          <Heading>
            Large and Compact <b>320 x 170</b>
          </Heading>

          <Cards>
            <LargeCard
              category='Concentrates'
              image={categoryStockPhotos.Concentrate[0]}
              radius={RadiusOption.rounded}
              mobileDisplay={CategoriesMobileDisplayOption.grid}
              isHidden={false}
            />

            <CompactCard
              category='Flower'
              image='https://images.dutchie.com/category-examples/flower.jpg'
              radius={RadiusOption.rounded}
              mobileDisplay={CategoriesMobileDisplayOption.grid}
              isHidden={false}
            />
          </Cards>
        </Example>

        <Example>
          <Heading>
            Stacked <b>320 x 320</b>
          </Heading>

          <Cards>
            <StackedCard
              category='Pre-Rolls'
              image={categoryStockPhotos['Pre-Rolls'][0]}
              radius={RadiusOption.roundedRectangle}
              mobileDisplay={CategoriesMobileDisplayOption.grid}
              isHidden={false}
            />

            <StackedCard
              category='Edibles'
              image='https://images.dutchie.com/category-examples/edibles.jpg'
              radius={RadiusOption.rounded}
              mobileDisplay={CategoriesMobileDisplayOption.grid}
              isHidden={false}
            />
          </Cards>
        </Example>
      </Examples>

      <VisuallyHidden>
        <input type='file' id='file' ref={fileInputRef} onChange={handleUploadFile} />
      </VisuallyHidden>

      <Button mt='0' onClick={handleClickFileInput} loading={isFileUploading}>
        Select File
      </Button>
    </ModalScreen>
  );
};

const Examples = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 64px 0;
  width: 100%;
`;

const Example = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: 372px;
`;

const Heading = styled.h3`
  font-size: 16px;
  font-weight: 400;
  line-height: ${20 / 16};
  margin-bottom: 24px;
  text-align: center;
  width: 100%;

  b {
    display: block;
  }
`;

const Cards = styled.ul`
  align-items: center;
  display: flex;
  justify-content: space-around;
  width: 100%;
`;
